// dateOnly.js — format a date-only value WITHOUT ever making it a Date.
//
// ─── THE BUG THIS EXISTS TO CLOSE ────────────────────────────────────
//
// Found 2026-09-12, on Liz Tucker's iLEAD Consent Form, mid-verification-pass.
// The document prints 04/07/2028. Pending Approvals showed "Expires Apr 6,
// 2028". The stored value was never wrong:
//
//   database   expiration_date = 2028-04-07        column type `date`
//   API        "2028-04-07T00:00:00.000Z"          <- a time it never had
//   browser    new Date(...) in Eastern            -> Apr 06 2028 20:00 EDT
//              .toLocaleDateString()               -> "Apr 6, 2028"
//
// `node-postgres` parses a `date` (type OID 1082) into a JS Date at LOCAL
// midnight, and the container has no TZ set, so that is midnight UTC.
// `res.json()` then serialises it as an instant. Every browser west of UTC
// reads that instant back as the previous day.
//
// It was uniform: all 53 expiration dates and all 55 completion dates in
// production rendered exactly one day behind, every month of the year, EST and
// EDT alike. One bug, one mechanism — the data was correct throughout.
//
// ─── THE RULE ────────────────────────────────────────────────────────
//
//   A DATE-ONLY VALUE MUST NEVER BECOME A JS Date.
//
// A `date` column has no time and no zone. The moment it becomes a Date it
// acquires both, and one of them is wrong. So these functions do string work:
// take the YYYY-MM-DD portion and format that. No Date construction, no
// local-noon parsing trick, no timezone special-casing — there is no timezone
// in the value to begin with, and the fix that introduces one is the fix that
// breaks again the next time something moves.
//
// The same page already did this correctly in its `approve()` prompt with
// `.slice(0, 10)`, which is why the chip and the prompt disagreed by a day and
// how we knew the stored value was fine.
//
// ─── RELATED, AND NOT REUSABLE ───────────────────────────────────────
//
// `src/utils/generateOfferLetter.js:47-59` diagnosed this exact bug once
// already and describes it accurately in its own comment. Its guard is
// `/^\d{4}-\d{2}-\d{2}$/`, which does NOT match what the API actually sends
// (a full ISO instant), so it cannot be reused as-is — but a third person
// finding this bug should land on both sites at once. If that helper is ever
// revisited, these functions are what it should become.
//
// ─── DO NOT "FINISH THE JOB" ─────────────────────────────────────────
//
// Three classes of site look like this bug and are NOT this bug:
//
//   1. Every `.slice(0, 10)` site. THEY ARE ALREADY RIGHT. They take the date
//      portion of the string and stop, which is exactly what this file does.
//
//   2. THE SERVER RENDERERS ARE CORRECT — `myFileController.js:81` and
//      `emailTemplates/onboardingExpiringReminder.js:13`. They run at TZ=UTC,
//      the same zone pg parsed the date in, so the round-trip is lossless and
//      they print the right day today. APPLYING A BROWSER-SHAPED FIX THERE
//      WOULD BREAK DATES THAT WORK, including the expiry notices in step 2.
//      This is a trap, not an oversight.
//
//   3. `completed_at` and anything else on a TIMESTAMP. Those hold a true
//      instant, so the UTC round-trip loses nothing and `new Date()` is
//      correct. On Pending Approvals "Submitted" renders correctly on the same
//      row where "Expires" did not — same page, same line, different column
//      type. Leave it alone.
//
// The real fix is one layer up: the API should not serialise a date-only
// column as an instant it never had. That has a wider blast radius, because it
// changes what the server renderers above receive. Recorded in
// `.handoff/2026-09-12-date-off-by-one.md`, deliberately not built.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Accepts either shape the portal actually carries — a bare `2028-04-07` or a
// full `2028-04-07T00:00:00.000Z` — and reads only the calendar part of it.
// Anything else returns null so a caller renders nothing rather than a wrong day.
function dateParts(value) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year: m[1], month, day };
}

/** "2028-04-07T00:00:00.000Z" -> "Apr 7, 2028". '' when there is no date. */
export function formatDateOnly(value) {
  const p = dateParts(value);
  if (!p) return '';
  return `${MONTHS[p.month - 1]} ${p.day}, ${p.year}`;
}

/** "2028-04-07T00:00:00.000Z" -> "4/7/2028". '' when there is no date. */
export function formatDateOnlyNumeric(value) {
  const p = dateParts(value);
  if (!p) return '';
  return `${p.month}/${p.day}/${p.year}`;
}
